function generatePenaltyReference() {
  const digits = String(Math.floor(100000 + Math.random() * 900000));
  const letters = Array.from({ length: 2 }, () =>
    String.fromCharCode(65 + Math.floor(Math.random() * 26))
  ).join('');
  return `PH-${digits}-${letters}`;
}

function formatIssuedLabel(date) {
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-GB', { month: 'short' }).toUpperCase();
  const year = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} ${hh}:${mm}`;
}

function formatRemainingLabel(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

document.addEventListener('alpine:init', () => {
  Alpine.data(
    'PenaltyNotice',
    ({
      delaySeconds,
      dismissDays,
      successDays,
      countdownMinutes,
      previewMode,
      enabled,
      errorText,
      serverErrorText,
    }) => ({
      delaySeconds,
      dismissDays,
      successDays,
      countdownMinutes,
      previewMode,
      enabled,
      errorText,
      serverErrorText,
      stage: 'notice',
      submitting: false,
      errorMessage: '',
      reference: '',
      issuedLabel: '',
      deadline: null,
      remainingLabel: '00:00',
      expired: false,
      sectionId: null,
      storageKey: null,
      sessionKey: null,
      ticketKey: null,
      _triggered: false,
      _cleanupTrigger: null,
      _tickInterval: null,
      init() {
        this.sectionId = this.$root.id;
        this.storageKey = `penalty-notice-${this.sectionId}`;
        this.sessionKey = `penalty-notice-session-${this.sectionId}`;
        this.ticketKey = `penalty-notice-ticket-${this.sectionId}`;

        if (window.location.pathname === '/challenge') return;

        initTeleport(this.$root);

        Alpine.store('modals').register('penaltyNotice', 'popup');

        document.body.addEventListener('penalty-notice-is-closed', () => {
          this.onClosed();
        });

        this._tickInterval = setInterval(() => this.tick(), 1000);

        // Shopify's /contact endpoint requires a CAPTCHA token that is
        // only generated during a genuine browser form submission — an
        // AJAX-intercepted POST can never satisfy it. So the "settle"
        // form submits for real (see onSubmit), and the outcome is
        // read back here from the fresh server-rendered markup after
        // the resulting page load, exactly like the theme's own
        // promo-popup.js already does for its newsletter form.
        const fragment = this.$refs.teleported.content;
        const successMarker = fragment.querySelector(
          '[data-penalty-notice-success]'
        );
        const errorMarker = fragment.querySelector(
          '[data-penalty-notice-error]'
        );
        const hasSuccess = Boolean(successMarker);
        const hasError = Boolean(errorMarker);

        if (hasError) {
          console.warn(
            'PenaltyNotice: Shopify rejected the submission —',
            errorMarker.textContent || '(no message)'
          );
        }

        if (hasSuccess || hasError) {
          this.ensureTicket();
          this.stage = hasSuccess ? 'success' : 'notice';
          this.errorMessage = hasSuccess ? '' : this.errorText;

          if (hasSuccess && !Shopify.designMode) {
            setExpiringStorageItem(
              this.storageKey,
              'subscribed',
              daysInMs(this.successDays)
            );
          }

          this.$store.modals.open('penaltyNotice');
          this.replayEntrance();
          setTimeout(() => this.$focus.first(), 50);
          this.clearSuccessParams();
          return;
        }

        if (!Shopify.designMode) {
          if (this.previewMode) {
            this.open();
            return;
          }

          // Any unexpired value — 'subscribed' (90-day) or 'dismissed'
          // (7-day) — means don't retrigger. getExpiringStorageItem
          // already returns null once past its own expiry.
          if (getExpiringStorageItem(this.storageKey)) return;
          if (sessionStorage.getItem(this.sessionKey)) return;

          const pageviewKey = 'phase-pageviews';
          const pageviews =
            parseInt(sessionStorage.getItem(pageviewKey) || '0', 10) + 1;
          sessionStorage.setItem(pageviewKey, String(pageviews));

          const trigger = () => {
            if (this._triggered) return;
            this._triggered = true;
            sessionStorage.setItem(this.sessionKey, '1');
            this.open();
          };

          const timer = setTimeout(
            trigger,
            Math.max(this.delaySeconds, 0) * 1000
          );

          let secondPageTimer = null;
          if (pageviews >= 2) {
            secondPageTimer = setTimeout(trigger, 2000);
          }

          const onScroll = () => {
            const scrolled = window.scrollY;
            const scrollable =
              document.documentElement.scrollHeight - window.innerHeight;

            if (scrollable > 0 && scrolled / scrollable > 0.5) {
              trigger();
            }
          };

          window.addEventListener('scroll', onScroll, { passive: true });

          this._cleanupTrigger = () => {
            clearTimeout(timer);
            if (secondPageTimer) clearTimeout(secondPageTimer);
            window.removeEventListener('scroll', onScroll);
          };
        } else {
          if (window.theme.designMode.selected === this.sectionId) {
            if (this.enabled === true) {
              this.open();
            } else {
              Alpine.store('modals').close('penaltyNotice');
            }
          }

          document.addEventListener(
            'shopify:section:select',
            this.onSectionSelect.bind(this)
          );

          document.addEventListener(
            'shopify:section:deselect',
            this.onSectionDeselect.bind(this)
          );
        }
      },
      tick() {
        if (!this.deadline) return;
        const remaining = this.deadline - Date.now();
        this.expired = remaining <= 0;
        this.remainingLabel = formatRemainingLabel(remaining);
      },
      ensureTicket() {
        const raw = sessionStorage.getItem(this.ticketKey);

        if (raw) {
          try {
            const data = JSON.parse(raw);
            if (data.reference && data.issuedLabel && data.deadline) {
              this.reference = data.reference;
              this.issuedLabel = data.issuedLabel;
              this.deadline = data.deadline;
              this.tick();
              return;
            }
          } catch (e) {}
        }

        const now = new Date();
        this.reference = generatePenaltyReference();
        this.issuedLabel = formatIssuedLabel(now);
        this.deadline = now.getTime() + this.countdownMinutes * 60 * 1000;

        sessionStorage.setItem(
          this.ticketKey,
          JSON.stringify({
            reference: this.reference,
            issuedLabel: this.issuedLabel,
            deadline: this.deadline,
          })
        );

        this.tick();
      },
      open() {
        this.ensureTicket();
        this.stage = 'notice';
        this.errorMessage = '';
        this.$store.modals.open('penaltyNotice');
        this.replayEntrance();
        setTimeout(() => this.$focus.first(), 50);
      },
      replayEntrance() {
        const el = this.$refs.ticket;
        if (!el) return;
        el.classList.remove('is-entering');
        void el.offsetWidth;
        el.classList.add('is-entering');
      },
      close() {
        this.$store.modals.close('penaltyNotice');
      },
      onCloseClick() {
        if (this.stage === 'notice') {
          this.stage = 'confirm';
          return;
        }
        this.close();
      },
      returnToNotice() {
        this.stage = 'notice';
      },
      onClosed() {
        if (this.stage !== 'success' && !Shopify.designMode) {
          setExpiringStorageItem(
            this.storageKey,
            'dismissed',
            daysInMs(this.dismissDays)
          );
        }
      },
      onSubmit(event) {
        const form = event.target;
        const emailInput = form.querySelector('input[name="contact[email]"]');
        const value = ((emailInput && emailInput.value) || '').trim();

        // Deliberately permissive — this is a signup form, not an
        // RFC 5322 validator. Anything with an @ and a . after it is
        // accepted; Shopify's own server-side handling is the real
        // authority on whether an address is usable.
        const looksLikeEmail = /^\S+@\S+\.\S+$/.test(value);

        if (!looksLikeEmail) {
          event.preventDefault();
          this.errorMessage = this.errorText;
          return;
        }

        // Valid — let the browser submit for real (needed for
        // Shopify's CAPTCHA check to pass). The result is picked up
        // from the reloaded page in init() above.
        this.errorMessage = '';
        this.submitting = true;
      },
      clearSuccessParams() {
        /**
         * After a successful submission, the URL becomes
         * `?customer_posted=true#contact_form`, which would cause the
         * notice to look "freshly submitted" again on a plain reload.
         */
        const updatedParams = new URLSearchParams(window.location.search);

        if (updatedParams.has('customer_posted')) {
          updatedParams.delete('customer_posted');
        }

        let newURL;

        if (updatedParams.toString().length > 0) {
          newURL = `${window.location.pathname}?${updatedParams.toString()}`;
        } else {
          newURL = window.location.pathname;
        }

        history.replaceState('', document.title, newURL);
      },
      onSectionSelect(e) {
        if (!e.target.contains(this.$root)) return;

        if (!this.enabled) return;

        this.open();
      },
      onSectionDeselect(e) {
        if (!e.target.contains(this.$root)) return;

        this.$store.modals.close('penaltyNotice');
      },
      destroy() {
        if (this._cleanupTrigger) this._cleanupTrigger();
        if (this._tickInterval) clearInterval(this._tickInterval);

        document.removeEventListener(
          'shopify:section:select',
          this.onSectionSelect
        );

        document.removeEventListener(
          'shopify:section:deselect',
          this.onSectionDeselect
        );
      },
    })
  );
});
